import React, { useEffect } from "react";
import {
  useFormContext,
  Controller,
  FieldValues,
  FieldErrors,
} from "react-hook-form";
import { TimePicker } from "antd";
import dayjs from "dayjs";
import { Button } from "../../components/Component";

interface FormTimePickerProps {
  name: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  format?: string;
  placeholder?: string;
  onChange?: (value: string) => void;
  defaultValue?: any;
}

const FormTimePicker: React.FC<FormTimePickerProps> = ({
  name,
  label,
  required = false,
  disabled = false,
  className = "",
  format = "HH:mm",
  placeholder = "Select Time",
  onChange,
  defaultValue,
}) => {
  const {
    register,
    setValue,
    control,
    formState: { errors },
  } = useFormContext();

  useEffect(() => {
    register(name, {
      required: required ? "Time is required" : undefined,
    });
  }, [register, name, required]);

  const errorMessage = getErrorMessage(errors, name);

  return (
    <div className={`form-group ${className}`}>
      {label && (
        <label className="form-label">
          {label} {required && <span className="mandatory">*</span>}
        </label>
      )}
      <div className="form-control-wrap">
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <TimePicker
              {...field}
              allowClear={!required}
              format={format}
              disabled={disabled}
              value={field.value ? dayjs(field.value, format) : null}
              onChange={(time) => {
                const formattedTime = time ? time.format(format) : "";
                field.onChange(formattedTime);
                setValue(name, formattedTime);
                onChange?.(formattedTime);
              }}
              placeholder={placeholder}
              style={{ width: "100%", height: "38px" }}
            />
          )}
        />
        {errorMessage && <p className="mandatory">{errorMessage}</p>}
      </div>
    </div>
  );
};

// Function to extract error messages from react-hook-form errors
function getErrorMessage<T extends FieldValues>(
  errors: FieldErrors<T>,
  fieldName: string
): string | undefined {
  const parts = fieldName.split(/[\[\].]+/).filter(Boolean);
  let currentError: any = errors;

  for (const part of parts) {
    if (currentError && typeof currentError === "object") {
      currentError = currentError[part];
    } else {
      return undefined;
    }
  }

  if (
    currentError &&
    "message" in currentError &&
    typeof currentError.message === "string"
  ) {
    return currentError.message;
  }

  return undefined;
}

export default FormTimePicker;
